import { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { MoreHorizontal, FolderInput, Copy, Trash2, Pencil, ChevronRight, Lock } from 'lucide-react';
import useBoardStore from '../../../store/boardStore';
import useAuthStore from '../../../store/authStore';
import { dialog } from '../../../store/dialogStore';
import useToastStore from '../../../store/toastStore';
import { canManageBoardEntry, canManageBoardProtection } from '../../../utils/permissions';
import { boardProtectionDialog } from '../../../store/boardProtectionDialogStore';

const MENU_WIDTH = 192; // w-48
const MENU_MAX_HEIGHT = 280; // estimativa pra flip-up

export default function BoardItemMenu({
  board,
  onDuplicate,
  onDelete,
  extraItems = [],
  // Modo controlado por coordenadas (clique-direito): quando anchorPoint != null,
  // o menu abre posicionado no cursor em vez de relativo ao botão "...".
  anchorPoint = null,
  onClose,
  hideButton = false,
}) {
  const { workspaces, moveBoardToWorkspace, moveBoardToWorkspaceFolder, workspaceFolders, fetchWorkspaceFolders, updateBoard } = useBoardStore();
  const showToast = useToastStore((s) => s.show);
  const user = useAuthStore((s) => s.user);
  // "Proteger" mora DENTRO deste componente (nao em extraItems) de proposito:
  // os dois lugares que montam este menu — AppLayout e WorkspaceFolder —
  // ganham o item sem repetir a regra de permissao em cada um, e uma mudanca
  // futura na regra nao precisa ser aplicada duas vezes.
  const canProtect = canManageBoardProtection(board, user);
  // Renomear e "Mover para..." continuam exatamente com a visibilidade de
  // hoje. Antes desta feature, este menu inteiro so era MONTADO quando
  // canManageBoardEntry passava (AppLayout/WorkspaceFolder); agora ele
  // tambem e montado para quem so pode proteger — o dono nao-admin de um
  // quadro, que tem nivel 'owner' mas cujo papel nao tem
  // board.archive/board.delete. Sem este gate, essas duas pessoas passariam
  // a ver dois itens que o backend recusaria.
  const canManageEntry = canManageBoardEntry(board, user);
  const [openUncontrolled, setOpenUncontrolled] = useState(false);
  const controlled = anchorPoint != null;
  const open = controlled ? true : openUncontrolled;
  const setOpen = (v) => {
    if (controlled) {
      const next = typeof v === 'function' ? v(true) : v;
      if (!next) onClose?.();
    } else {
      setOpenUncontrolled(v);
    }
  };
  const [moving, setMoving] = useState(false);
  // selectedWorkspace: { id, name } do workspace escolhido antes de mostrar folders
  const [selectedWorkspace, setSelectedWorkspace] = useState(null);
  const [loadingFolders, setLoadingFolders] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const btnRef = useRef(null);
  const popRef = useRef(null);
  const btnRectRef = useRef(null);

  // Posicionamento via portal: no cursor (modo controlado) ou à direita do botão
  useLayoutEffect(() => {
    if (!open) return;
    const recompute = () => {
      const popHeight = popRef.current?.offsetHeight || 0;
      const popWidth = popRef.current?.offsetWidth || MENU_WIDTH;
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      if (controlled) {
        // Posiciona no cursor, com flip se estourar viewport
        const x = anchorPoint.x;
        const y = anchorPoint.y;
        let left = x;
        if (left + popWidth > vw - 8) left = Math.max(8, x - popWidth);
        let top = y;
        if (popHeight > 0 && top + popHeight > vh - 8) top = Math.max(8, y - popHeight);
        setPos({ top: top + window.scrollY, left: left + window.scrollX });
        return;
      }
      if (!btnRef.current) return;
      // O botão pode ficar display:none quando o hover some (hidden group-hover:flex).
      // Nesse caso getBoundingClientRect retorna zeros — usamos o rect cacheado.
      const live = btnRef.current.getBoundingClientRect();
      if (live.width > 0) btnRectRef.current = live;
      const rect = btnRectRef.current;
      if (!rect) return;
      const spaceBelow = vh - rect.bottom;
      const flipUp = popHeight > 0 && spaceBelow < (popHeight + 8) && rect.top > spaceBelow;
      const top = flipUp
        ? rect.top + window.scrollY - popHeight - 4
        : rect.bottom + window.scrollY + 4;
      // Alinha à direita do botão (popup com w-48)
      const left = rect.right + window.scrollX - MENU_WIDTH;
      setPos({ top, left });
    };
    recompute();
    // Segundo recompute após render do popup, com altura real
    const raf = requestAnimationFrame(recompute);
    window.addEventListener('scroll', recompute, true);
    window.addEventListener('resize', recompute);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('scroll', recompute, true);
      window.removeEventListener('resize', recompute);
    };
  }, [open, moving, selectedWorkspace, controlled, anchorPoint]);

  useEffect(() => {
    if (!open) { btnRectRef.current = null; return; }
    const h = (e) => {
      const inBtn = btnRef.current && btnRef.current.contains(e.target);
      const inPop = popRef.current && popRef.current.contains(e.target);
      if (!inBtn && !inPop) { setOpen(false); setMoving(false); setSelectedWorkspace(null); }
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);

  const closeAll = () => { setOpen(false); setMoving(false); setSelectedWorkspace(null); };

  const doRename = async () => {
    setOpen(false);
    const name = await dialog.prompt('Renomear quadro', { defaultValue: board.name, placeholder: 'Nome do quadro', confirmLabel: 'Renomear' });
    if (!name || name.trim() === board.name) return;
    try { await updateBoard(board.id, { name: name.trim() }); }
    catch (err) { dialog.alert('Erro ao renomear', err.message); }
  };

  // Passo 1: usuário escolhe workspace — verifica se há folders; se sim, mostra sub-nível
  const handleWorkspaceClick = async (ws) => {
    if (ws.id === board.workspace_id && board.folder_id == null) {
      // já está nesse workspace sem folder — ignora
      closeAll();
      return;
    }
    const foldersForWs = workspaceFolders.filter((f) => f.workspace_id === ws.id);
    if (foldersForWs.length > 0) {
      // mostra sub-nível de folders
      setSelectedWorkspace(ws);
      return;
    }
    // sem folders carregados — tenta buscar do servidor
    setLoadingFolders(true);
    try {
      const fetched = await fetchWorkspaceFolders(ws.id);
      if (fetched && fetched.length > 0) {
        setLoadingFolders(false);
        setSelectedWorkspace(ws);
        return;
      }
    } catch {
      // ignora erro de fetch e move direto
    }
    setLoadingFolders(false);
    // sem folders: move direto para a raiz do workspace
    await doMoveToWorkspace(ws);
  };

  // Mover para a raiz de um workspace (sem folder)
  const doMoveToWorkspace = async (ws) => {
    const originalWorkspaceId = board.workspace_id;
    const originalFolderId = board.folder_id;
    try {
      await moveBoardToWorkspace(board.id, ws.id);
      // se tinha folder, remove do folder (mover workspace já limpa folder_id no backend via PUT workspace_id)
      closeAll();
      showToast({
        message: `Você moveu '${board.name}' para '${ws.name}'`,
        onUndo: async () => {
          try {
            await moveBoardToWorkspace(board.id, originalWorkspaceId);
            // se havia folder original, restaura
            if (originalFolderId != null) {
              await moveBoardToWorkspaceFolder(board.id, originalFolderId);
            }
          } catch (err) {
            dialog.alert('Erro ao desfazer', err.message);
          }
        },
      });
    } catch (err) {
      dialog.alert('Erro ao mover', err.message);
      closeAll();
    }
  };

  // Passo 2: usuário escolhe folder (ou raiz) dentro do workspace selecionado
  const doMoveToFolder = async (folderId) => {
    const ws = selectedWorkspace;
    const originalWorkspaceId = board.workspace_id;
    const originalFolderId = board.folder_id;
    const folderName = folderId
      ? workspaceFolders.find((f) => f.id === folderId)?.name
      : null;
    const destLabel = folderName ? `'${folderName}' em '${ws.name}'` : `'${ws.name}'`;
    try {
      // Se mudou de workspace, move o workspace primeiro
      if (ws.id !== board.workspace_id) {
        await moveBoardToWorkspace(board.id, ws.id);
      }
      // Define o folder (null = raiz)
      await moveBoardToWorkspaceFolder(board.id, folderId);
      closeAll();
      showToast({
        message: `Você moveu '${board.name}' para ${destLabel}`,
        onUndo: async () => {
          try {
            if (originalWorkspaceId !== ws.id) {
              await moveBoardToWorkspace(board.id, originalWorkspaceId);
            }
            await moveBoardToWorkspaceFolder(board.id, originalFolderId);
          } catch (err) {
            dialog.alert('Erro ao desfazer', err.message);
          }
        },
      });
    } catch (err) {
      dialog.alert('Erro ao mover', err.message);
      closeAll();
    }
  };

  // Folders do workspace selecionado no sub-nível
  const foldersForSelected = selectedWorkspace
    ? workspaceFolders.filter((f) => f.workspace_id === selectedWorkspace.id)
    : [];

  const popup = open ? (
    <div
      ref={popRef}
      style={{ position: 'absolute', top: pos.top, left: pos.left, width: MENU_WIDTH, zIndex: 9999 }}
      className="bg-card border border-border rounded-lg shadow-xl py-1"
    >
      {!moving ? (
        <>
          {canManageEntry && (
            <>
              <button
                onClick={(e) => { e.stopPropagation(); doRename(); }}
                className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-text-primary hover:bg-white/5"
              >
                <Pencil className="w-3.5 h-3.5" />
                Renomear
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); setMoving(true); setSelectedWorkspace(null); }}
                className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-text-primary hover:bg-white/5"
              >
                <FolderInput className="w-3.5 h-3.5" />
                Mover para...
              </button>
            </>
          )}
          {canProtect && (
            <button
              onClick={(e) => { e.stopPropagation(); boardProtectionDialog.open(board); setOpen(false); }}
              className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-text-primary hover:bg-white/5"
            >
              <Lock className="w-3.5 h-3.5" />
              {board.is_protected ? 'Senha do quadro' : 'Proteger'}
            </button>
          )}
          {extraItems.map((it, i) => (
            <button
              key={i}
              onClick={(e) => { e.stopPropagation(); setOpen(false); it.onClick(); }}
              className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-text-primary hover:bg-white/5"
            >
              {it.icon && <it.icon className="w-3.5 h-3.5" />}
              {it.label}
            </button>
          ))}
          {onDuplicate && (
            <button
              onClick={(e) => { e.stopPropagation(); setOpen(false); onDuplicate(); }}
              className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-text-primary hover:bg-white/5"
            >
              <Copy className="w-3.5 h-3.5" />
              Duplicar
            </button>
          )}
          {onDelete && (
            <button
              onClick={(e) => { e.stopPropagation(); setOpen(false); onDelete(); }}
              className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-danger hover:bg-danger/10"
            >
              <Trash2 className="w-3.5 h-3.5" />
              Excluir
            </button>
          )}
        </>
      ) : selectedWorkspace ? (
        // Sub-nível: lista de folders do workspace escolhido
        <>
          <button
            onClick={(e) => { e.stopPropagation(); setSelectedWorkspace(null); }}
            className="w-full flex items-center gap-1.5 px-3 py-1.5 text-[10px] text-text-secondary hover:bg-white/5"
          >
            <ChevronRight className="w-3 h-3 rotate-180" />
            {selectedWorkspace.name}
          </button>
          <div className="border-t border-border/40 my-0.5" />
          <div className="max-h-60 overflow-y-auto">
            <button
              onClick={(e) => { e.stopPropagation(); doMoveToFolder(null); }}
              className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-text-primary hover:bg-white/5"
            >
              <span className="truncate">Raiz do workspace</span>
              {board.workspace_id === selectedWorkspace.id && board.folder_id == null && (
                <span className="ml-auto text-[10px] text-accent">(atual)</span>
              )}
            </button>
            {foldersForSelected.map((folder) => (
              <button
                key={folder.id}
                onClick={(e) => { e.stopPropagation(); doMoveToFolder(folder.id); }}
                className={`w-full flex items-center gap-2 px-3 py-1.5 text-xs transition-colors ${
                  board.folder_id === folder.id
                    ? 'text-accent'
                    : 'text-text-primary hover:bg-white/5'
                }`}
              >
                <span className="truncate">{folder.name}</span>
                {board.folder_id === folder.id && (
                  <span className="ml-auto text-[10px]">(atual)</span>
                )}
              </button>
            ))}
          </div>
        </>
      ) : (
        // Nível 1: lista de workspaces
        <>
          <div className="px-3 py-1 text-[10px] font-semibold text-text-secondary uppercase tracking-wider">
            Mover para área
          </div>
          <div className="max-h-60 overflow-y-auto">
            {loadingFolders ? (
              <div className="px-3 py-2 text-xs text-text-secondary">Carregando...</div>
            ) : (
              workspaces.map((ws) => {
                const hasFolders = workspaceFolders.some((f) => f.workspace_id === ws.id);
                return (
                  <button
                    key={ws.id}
                    onClick={(e) => { e.stopPropagation(); handleWorkspaceClick(ws); }}
                    className={`w-full flex items-center gap-2 px-3 py-1.5 text-xs transition-colors ${
                      ws.id === board.workspace_id
                        ? 'text-accent'
                        : 'text-text-primary hover:bg-white/5'
                    }`}
                  >
                    <span className="truncate flex-1">{ws.name}</span>
                    {ws.id === board.workspace_id && !hasFolders && (
                      <span className="ml-auto text-[10px]">(atual)</span>
                    )}
                    {hasFolders && <ChevronRight className="w-3 h-3 shrink-0 text-text-secondary" />}
                  </button>
                );
              })
            )}
          </div>
        </>
      )}
    </div>
  ) : null;

  return (
    <>
      {!hideButton && (
        <button
          ref={btnRef}
          onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); setMoving(false); setSelectedWorkspace(null); }}
          className="p-1 rounded hover:bg-white/10 text-text-secondary hover:text-text-primary transition-colors"
          title="Opções"
        >
          <MoreHorizontal className="w-3 h-3" />
        </button>
      )}
      {popup && createPortal(popup, document.body)}
    </>
  );
}
